// Arithmetic for the report page's "Omsetning / Betalinger / Avstemming" block.
//
// Omsetning (sales) is recognised on the sale document date (saleDate) and is what the
// bookkeeping and VAT return are built on (bokføringsloven § 4, merverdiavgiftsloven § 15-9).
// Betalinger (payments) is money actually received in the period (salePayment.paidDate).
// The two are kept in separate objects here so no caller can add a payment into a sales total.

const num = value => Number(value) || 0;
const cents = value => Math.round(num(value) * 100);
const fromCents = value => value / 100;

// Payment methods that have their own column in the Betalinger table. Everything else the
// payment ledger contains (insurance, roundoff, creditNote refunds, convene, unpaid, ...)
// is summed into the "Annet" column so the total always equals the ledger for the period.
export const KNOWN_PAYMENT_METHODS = ['cash', 'card', 'cardExternal', 'creditCard', 'vipps', 'account', 'invoice'];

export function otherPaymentsTotal(aggSaleTotalPaymentMethodsNew) {
    if (!Array.isArray(aggSaleTotalPaymentMethodsNew)) {
        return 0;
    }
    const total = aggSaleTotalPaymentMethodsNew
        .filter(item => !KNOWN_PAYMENT_METHODS.includes(item.paymentMethod))
        .reduce((sum, item) => sum + cents(item.price), 0);
    return fromCents(total);
}

export function computeReportTotals(saleReport = {}, { showCreditCard = false, showConvene = false } = {}) {
    const creditCardSales = showCreditCard ? cents(saleReport.saleAmountCreditCard) : 0;
    const creditCardPayments = showCreditCard ? cents(saleReport.amountCreditCard) : 0;
    // Convene gets its own kredittsalg column only for clinics with Convene enabled. Without the
    // flag the figures stay where the store puts them: sales in the "uten betalingsmåte" remainder,
    // payments inside amountOtherPaid ("Annet"), so nothing is lost either way.
    const conveneSales = showConvene ? cents(saleReport.saleAmountConvene) : 0;
    const convenePayments = showConvene ? cents(saleReport.amountConvene) : 0;

    // --- Omsetning (sale-based) ---
    const cashSales = cents(saleReport.saleAmountCash) + cents(saleReport.saleAmountCard)
        + creditCardSales + cents(saleReport.saleAmountVipps);
    const creditSales = cents(saleReport.amountInvoice) + cents(saleReport.saleAmountAccount)
        + cents(saleReport.amountPartial) + conveneSales;
    const corrections = cents(saleReport.amountCreditNote) + cents(saleReport.amountSaleCredited);
    const turnover = cents(saleReport.amountTotal);
    const salesWithoutMethod = turnover - (cashSales + creditSales + corrections);

    // --- Betalinger (payment-based) ---
    const cashPayments = cents(saleReport.amountCash) + cents(saleReport.amountCard)
        + creditCardPayments + cents(saleReport.amountVipps);
    const otherPayments = cents(saleReport.amountOtherPaid) - convenePayments;
    const creditPayments = cents(saleReport.amountAccount) + cents(saleReport.amountInvoicePaid)
        + convenePayments + otherPayments;
    const paymentsTotal = cashPayments + creditPayments;

    return {
        cashSales: fromCents(cashSales),
        creditSales: fromCents(creditSales),
        corrections: fromCents(corrections),
        turnover: fromCents(turnover),
        salesWithoutMethod: fromCents(salesWithoutMethod),
        conveneSales: fromCents(conveneSales),
        convenePayments: fromCents(convenePayments),
        otherPayments: fromCents(otherPayments),
        cashPayments: fromCents(cashPayments),
        creditPayments: fromCents(creditPayments),
        paymentsTotal: fromCents(paymentsTotal),
        cashDifference: fromCents(cashPayments - cashSales),
        creditDifference: fromCents(creditPayments - creditSales),
    };
}

// Breaks the Avstemming "Forskjell kontantsalg" down using the cash-flow analysis endpoint
// (/api/sales/report/cashflow-analysis). `cashDifference` is the figure from computeReportTotals.
export function explainCashDifference(cashflowAnalysis, cashDifference) {
    const a = cashflowAnalysis || {};
    const previousPeriods = cents(a.increasesActual?.paymentsFromPreviousPeriods?.total);
    const creditSalesPaidAtCounter = cents(a.increasesActual?.paymentsForInvoiceSales?.total);
    const overpaid = cents(a.increasesActual?.overpaidCashFlowSales?.total);
    const unpaidCashSales = cents(a.decreasesActual?.unpaidCashFlowSales?.total);
    const calculatedDiff = previousPeriods + creditSalesPaidAtCounter + overpaid - unpaidCashSales;
    return {
        previousPeriods: fromCents(previousPeriods),
        creditSalesPaidAtCounter: fromCents(creditSalesPaidAtCounter),
        overpaid: fromCents(overpaid),
        unpaidCashSales: fromCents(unpaidCashSales),
        calculatedDiff: fromCents(calculatedDiff),
        unexplained: fromCents(cents(cashDifference) - calculatedDiff),
    };
}
